import { useMemo, useState } from 'react';

// Accent- and case-insensitive text for the search.
const fold = text => (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const MAX_CATEGORIES = 8;
// Locked: the gallery is drawn from placeholders (`trendingSample_<n>` copy) under a blur.
const SAMPLES = [1, 2, 3, 4, 5, 6];
const SAMPLE_CATEGORIES = [1, 2, 3, 4, 5];

// Trending topics are the Pro plan's (`features.trending_topics`). With them: the ranked
// topics, filtered by text and by categories (the topics' `niche_tags`, most used first;
// any selected category matches). Without them: placeholders, and the way to the plan
// that has them (sign-in first for a visitor).
export default function useTrending(s) {
  const { t, auth, features, payments: p } = s;
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const loading = auth.loading || (auth.signed && s.accountStatus === 'loading');
  const unlocked = auth.signed && !!features.trending_topics;
  // Topics are the creator's; until the first profile is connected there are none.
  const needsProfile = unlocked && (!s.snapshot.creator || s.snapshot.creator.onboarding_status === 'pending');

  const tags = useMemo(() => {
    const counts = new Map();
    s.topics.forEach(topic => (topic.niche_tags || []).forEach(tag => counts.set(tag, (counts.get(tag) || 0) + 1)));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_CATEGORIES).map(([tag]) => tag);
  }, [s.topics]);
  const topics = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    return s.topics.filter(topic => {
      if (categories.length && !(topic.niche_tags || []).some(tag => categories.includes(tag))) return false;
      const text = fold([topic.title, topic.relevance_reason, topic.trend_evidence, ...(topic.niche_tags || [])].join(' '));
      return words.every(word => text.includes(word));
    });
  }, [s.topics, query, categories]);
  const toggleCategory = tag => setCategories(list => (list.includes(tag) ? list.filter(item => item !== tag) : [...list, tag]));
  const clearCategories = () => setCategories([]);
  const clearFilters = () => { setQuery(''); setCategories([]); };
  const filtering = !!query.trim() || categories.length > 0;

  const rank = p.planInfo(p.plan)?.rank ?? 0;
  const upgradeTo = p.enabled ? p.catalog?.plans.find(item => item.features?.trending_topics && item.rank > rank)?.plan || null : null;
  const unlock = !auth.signed ? s.goToLogin : upgradeTo ? () => p.openUpgrade(upgradeTo) : null;
  const samples = SAMPLES.map(n => ({ id: n, title: t(`trendingSample_${n}`), category: t(`trendingCategory_${((n - 1) % SAMPLE_CATEGORIES.length) + 1}`) }));
  const sampleCategories = SAMPLE_CATEGORIES.map(n => t(`trendingCategory_${n}`));

  return { loading, unlocked, needsProfile, query, setQuery, categories, toggleCategory, clearCategories, clearFilters, filtering, tags, topics,
    upgradeTo, unlock, samples, sampleCategories };
}
